import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DocumentToolbar = ({
  viewMode,
  onViewModeChange,
  onUpload,
  onCreateFolder,
  onBulkDelete,
  onRefresh,
  selectedDocuments,
  currentFolder,
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange
}) => {
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);

  const sortOptions = [
    { value: 'name', label: 'Name', icon: 'Type' },
    { value: 'uploadDate', label: 'Date Modified', icon: 'Calendar' },
    { value: 'size', label: 'File Size', icon: 'HardDrive' },
    { value: 'type', label: 'File Type', icon: 'FileType' }
  ];

  const handleBulkAction = (action) => {
    switch (action) {
      case 'delete':
        onBulkDelete(selectedDocuments);
        break;
      case 'download':
        // Handle bulk download
        console.log('Bulk download:', selectedDocuments);
        break;
      case 'move':
        // Handle bulk move
        console.log('Bulk move:', selectedDocuments);
        break;
      default:
        break;
    }
    setShowBulkActions(false);
  };

  const handleClearSelection = () => {
    // This will be handled by parent component through onSelectionChange
    // We need to pass this through props
    if (window.confirm('Clear selected documents?')) {
      window.location?.reload();
    }
  };

  return (
    <div className="bg-card border-b border-border p-4">
      {/* Main Toolbar */}
      <div className="flex items-center justify-between gap-4 mb-4">
        {/* Left Section - Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            iconName="Upload"
            iconPosition="left"
            iconSize={16}
            onClick={onUpload}
          >
            Upload
          </Button>
          
          <Button
            variant="outline"
            iconName="FolderPlus"
            iconPosition="left"
            iconSize={16}
            onClick={onCreateFolder}
          >
            New Folder
          </Button>

          <div className="h-6 w-px bg-border mx-2" />

          <Button
            variant="ghost"
            size="icon"
            iconName="RefreshCw"
            iconSize={18}
            onClick={onRefresh}
          />

          {/* Sort Menu */}
          <div className="relative">
            <Button
              variant="ghost"
              iconName="ArrowUpDown"
              iconPosition="left"
              iconSize={16}
              onClick={() => setShowSortMenu(!showSortMenu)}
            >
              Sort
            </Button>

            {showSortMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowSortMenu(false)}
                />
                <div className="absolute top-full left-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-lg z-50">
                  <div className="p-2">
                    {sortOptions?.map((option) => (
                      <button
                        key={option?.value}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors ${
                          sortBy === option?.value ? 'bg-muted text-foreground' : 'text-muted-foreground'
                        }`}
                        onClick={() => {
                          onSortChange(option?.value);
                          setShowSortMenu(false);
                        }}
                      >
                        <Icon name={option?.icon} size={16} />
                        {option?.label}
                        {sortBy === option?.value && (
                          <Icon name="Check" size={16} className="ml-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Section - View Controls */}
        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative w-64 hidden md:block">
            <Input
              type="search"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e?.target?.value)}
              className="pl-9"
            />
            <Icon 
              name="Search" 
              size={16} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center bg-muted rounded-lg p-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              iconName="Grid3X3"
              iconSize={16}
              onClick={() => onViewModeChange('grid')}
              className="px-3"
            />
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              iconName="List"
              iconSize={16}
              onClick={() => onViewModeChange('list')}
              className="px-3"
            />
          </div>
        </div>
      </div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <Icon name="Home" size={16} />
        <span>Document Bases</span>
        {currentFolder && (
          <>
            <Icon name="ChevronRight" size={14} />
            <span className="text-foreground font-medium">{currentFolder?.name}</span>
          </>
        )}
      </div>
      {/* Bulk Actions Bar */}
      {selectedDocuments?.length > 0 && (
        <div className="flex items-center justify-between bg-primary/10 border border-primary/20 rounded-lg p-3">
          <div className="flex items-center gap-3">
            <Icon name="CheckCircle" size={20} className="text-primary" />
            <span className="font-medium text-foreground">
              {selectedDocuments?.length} document{selectedDocuments?.length !== 1 ? 's' : ''} selected
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              iconName="Download"
              iconPosition="left"
              iconSize={16}
              onClick={() => handleBulkAction('download')}
            >
              Download
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              iconName="Move"
              iconPosition="left"
              iconSize={16}
              onClick={() => handleBulkAction('move')}
            >
              Move
            </Button>

            <Button
              variant="ghost"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              iconSize={16}
              onClick={() => handleBulkAction('delete')}
              className="text-error hover:text-error hover:bg-destructive/10"
            >
              Delete
            </Button>

            <div className="h-4 w-px bg-border mx-2" />

            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              iconSize={16}
              onClick={handleClearSelection}
            >
              Clear
            </Button>
          </div>
        </div>
      )}
      {/* Mobile Search */}
      <div className="md:hidden">
        <Input
          type="search"
          placeholder="Search documents..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e?.target?.value)}
          className="w-full"
        />
      </div>
    </div>
  );
};

export default DocumentToolbar;